import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CampaignPerformance from "@/components/CampaignPerformance";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, Megaphone, PencilSimple, Trash, MagnifyingGlass, Target, Wallet, Notebook, Package as PackageIcon, Sparkle, Stack, FileText, ChartLineUp, Brain, Storefront,
} from "@phosphor-icons/react";
import CampaignStrategy from "@/components/CampaignStrategy";
import CampaignPillars from "@/components/CampaignPillars";
import CampaignContent from "@/components/CampaignContent";
import CampaignIntelligence from "@/components/CampaignIntelligence";
import CampaignLandingPages from "@/components/CampaignLandingPages";

const STATUSES = [
  { value: "draft", label: "Draft", ring: "border-white/15 text-ink-secondary" },
  { value: "active", label: "Active", ring: "border-emerald-400/30 bg-emerald-400/5 text-emerald-200" },
  { value: "completed", label: "Completed", ring: "border-white/10 bg-white/5 text-ink-secondary" },
];

const emptyForm = {
  name: "", product_id: "", persona_id: "", type: "", target_audience: "", icp_persona: "",
  pain_point: "", usp: "", objective: "", desired_action: "", geography: "",
  budget: "", start_date: "", end_date: "", proof_points: "", existing_assets: "",
  preferred_channels: "", channel_ids: [], status: "draft",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

export default function Campaigns() {
  const { currentOrg } = useAuth();
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("__all__");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("basics");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, pers, ch] = await Promise.all([
        api.get("/campaigns"),
        api.get("/products").catch(() => ({ data: [] })),
        api.get("/personas").catch(() => ({ data: [] })),
        api.get("/channels").catch(() => ({ data: [] })),
      ]);
      setItems(c.data);
      setProducts((p.data || []).filter(x => x.is_active !== false));
      setPersonas(pers.data || []);
      setChannels(ch.data || []);
    } catch (err) {
      if (err?.response?.status === 403) toast.error("Campaigns isn't enabled for this workspace");
      else toast.error("Could not load campaigns");
      setItems([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const filtered = useMemo(() => {
    let list = items;
    if (statusFilter !== "__all__") list = list.filter(c => c.status === statusFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(c => (c.name || "").toLowerCase().includes(q));
    }
    return list;
  }, [items, query, statusFilter]);

  const openCreate = () => { setEditing(null); setEditingCampaign(null); setForm(emptyForm); setTab("basics"); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c.campaign_id);
    setEditingCampaign(c);
    setForm({
      name: c.name || "", product_id: c.product_id || "", persona_id: c.persona_id || "",
      type: c.type || "",
      target_audience: c.target_audience || "", icp_persona: c.icp_persona || "",
      pain_point: c.pain_point || "", usp: c.usp || "", objective: c.objective || "",
      desired_action: c.desired_action || "", geography: c.geography || "",
      budget: c.budget === null || c.budget === undefined ? "" : String(c.budget),
      start_date: c.start_date || "", end_date: c.end_date || "",
      proof_points: c.proof_points || "", existing_assets: c.existing_assets || "",
      preferred_channels: c.preferred_channels || "",
      channel_ids: Array.isArray(c.channel_ids) ? c.channel_ids : [],
      status: c.status || "draft",
    });
    setTab("basics");
    setDialogOpen(true);
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    let budget = null;
    if (form.budget !== "" && form.budget !== null && form.budget !== undefined) {
      const n = Number(form.budget);
      if (Number.isNaN(n) || n < 0) { toast.error("Budget must be a non-negative number"); return; }
      budget = n;
    }
    const payload = { ...form, budget, product_id: form.product_id || null, persona_id: form.persona_id || null, type: form.type || null, channel_ids: form.channel_ids || [] };
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/campaigns/${editing}`, payload);
        setItems((all) => all.map(x => x.campaign_id === editing ? data : x));
        setEditingCampaign(data);
        toast.success("Campaign updated");
      } else {
        const { data } = await api.post("/campaigns", payload);
        setItems((all) => [data, ...all]);
        toast.success("Campaign created");
      }
      setDialogOpen(false); setForm(emptyForm); setEditing(null); setEditingCampaign(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/campaigns/${confirmDelete.campaign_id}`);
      setItems(all => all.filter(x => x.campaign_id !== confirmDelete.campaign_id));
      toast.success(`Removed ${confirmDelete.name}`);
    } catch { toast.error("Could not remove"); }
    finally { setConfirmDelete(null); }
  };

  const statusPill = (s) => {
    const st = STATUSES.find(x => x.value === s) || STATUSES[0];
    return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${st.ring}`}>{st.label}</span>;
  };

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="campaigns-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">Workspace</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Campaigns</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-lg">
            Capture the brief for each marketing campaign — audience, message, budget and channels. Leads captured during a campaign can be linked back to it.
          </p>
        </div>
        <button data-testid="campaigns-add" onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-5 py-2.5 text-sm font-medium hover:bg-neutral-200">
          <Plus weight="bold" className="h-4 w-4" /> New campaign
        </button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-secondary" />
          <input data-testid="campaigns-search" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search by campaign name"
            className="w-full bg-graphite-800 border border-white/10 rounded-md pl-10 pr-3 py-2 text-sm text-ink-primary placeholder:text-ink-secondary/60 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/30" />
        </div>
        <div className="flex items-center gap-1" data-testid="campaigns-status-filter">
          {[{ value: "__all__", label: "All" }, ...STATUSES].map(s => (
            <button key={s.value} data-testid={`campaigns-status-${s.value}`}
              onClick={() => setStatusFilter(s.value)}
              className={`px-3 py-1.5 rounded-full border text-xs ${statusFilter === s.value ? "border-white/40 bg-white text-graphite-950" : "border-white/10 bg-graphite-800 text-ink-secondary hover:text-ink-primary"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-md border border-white/10 bg-graphite-900 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-ink-secondary">Loading campaigns…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center" data-testid="campaigns-empty">
            <Megaphone weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/50" />
            <div className="mt-4 font-display text-xl tracking-tight">
              {items.length === 0 ? "No campaigns yet" : "No matches"}
            </div>
            <p className="mt-2 text-sm text-ink-secondary max-w-sm mx-auto">
              {items.length === 0 ? "Create your first campaign brief." : "Try a different search or status."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="campaigns-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Product</th>
                  <th className="px-5 py-3 font-medium">Type</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Window</th>
                  <th className="px-5 py-3 font-medium text-right">Leads</th>
                  <th className="px-5 py-3 font-medium text-right">Customers</th>
                  <th className="px-5 py-3 font-medium text-right">Revenue</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map(c => (
                  <tr key={c.campaign_id} data-testid={`campaign-row-${c.campaign_id}`} className="hover:bg-white/[0.02]">
                    <td className="px-5 py-3 text-ink-primary" data-testid={`campaign-name-${c.campaign_id}`}>{c.name}</td>
                    <td className="px-5 py-3 text-ink-secondary">
                      {c.product_name ? (
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <PackageIcon weight="duotone" className="h-3 w-3" />
                          {c.product_name}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-5 py-3 text-ink-secondary">{c.type || "—"}</td>
                    <td className="px-5 py-3" data-testid={`campaign-status-${c.campaign_id}`}>{statusPill(c.status)}</td>
                    <td className="px-5 py-3 text-ink-secondary text-xs">
                      {c.start_date || c.end_date ? `${fmtDate(c.start_date)} → ${fmtDate(c.end_date)}` : "—"}
                    </td>
                    <td className="px-5 py-3 text-right text-ink-secondary tabular-nums" data-testid={`campaign-leads-${c.campaign_id}`}>
                      {c.perf_summary?.leads_count || 0}
                    </td>
                    <td className="px-5 py-3 text-right text-ink-secondary tabular-nums" data-testid={`campaign-customers-${c.campaign_id}`}>
                      {c.perf_summary?.customers_count || 0}
                    </td>
                    <td className="px-5 py-3 text-right text-ink-primary tabular-nums" data-testid={`campaign-revenue-${c.campaign_id}`}>
                      {(c.perf_summary?.revenue_total || 0) > 0
                        ? `$${(c.perf_summary.revenue_total).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                        : <span className="text-ink-secondary/50">—</span>}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button data-testid={`campaign-edit-${c.campaign_id}`} onClick={() => openEdit(c)}
                          className="p-2 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5"><PencilSimple weight="bold" className="h-3.5 w-3.5" /></button>
                        <button data-testid={`campaign-delete-${c.campaign_id}`} onClick={() => setConfirmDelete(c)}
                          className="p-2 rounded-md text-ink-secondary hover:text-accent-danger hover:bg-white/5"><Trash weight="bold" className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!saving) { setDialogOpen(v); if (!v) { setForm(emptyForm); setEditing(null); setEditingCampaign(null); } } }}>
        <DialogContent data-testid="campaign-dialog" className="max-w-4xl bg-graphite-900 border-white/10 text-ink-primary max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">{editing ? "Edit campaign" : "New campaign brief"}</DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">Capture the brief now — later releases will use it to generate strategy, content and reports.</DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className={`grid ${editing ? "grid-cols-10" : "grid-cols-4"} bg-graphite-950 border border-white/10`}>
              <TabsTrigger value="basics" data-testid="campaign-tab-basics"><Notebook weight="duotone" className="h-3.5 w-3.5 mr-1.5" /> Basics</TabsTrigger>
              <TabsTrigger value="audience" data-testid="campaign-tab-audience"><Target weight="duotone" className="h-3.5 w-3.5 mr-1.5" /> Audience & Message</TabsTrigger>
              <TabsTrigger value="budget" data-testid="campaign-tab-budget"><Wallet weight="duotone" className="h-3.5 w-3.5 mr-1.5" /> Budget & Timeline</TabsTrigger>
              <TabsTrigger value="assets" data-testid="campaign-tab-assets"><Megaphone weight="duotone" className="h-3.5 w-3.5 mr-1.5" /> Assets & Channels</TabsTrigger>
              {editing && (
                <TabsTrigger value="performance" data-testid="campaign-tab-performance"><ChartLineUp weight="duotone" className="h-3.5 w-3.5 mr-1.5" /> Performance</TabsTrigger>
              )}
              {editing && (
                <TabsTrigger value="pillars" data-testid="campaign-tab-pillars"><Stack weight="duotone" className="h-3.5 w-3.5 mr-1.5" /> Pillars</TabsTrigger>
              )}
              {editing && (
                <TabsTrigger value="content" data-testid="campaign-tab-content"><FileText weight="duotone" className="h-3.5 w-3.5 mr-1.5" /> Content</TabsTrigger>
              )}
              {editing && (
                <TabsTrigger value="strategy" data-testid="campaign-tab-strategy"><Sparkle weight="duotone" className="h-3.5 w-3.5 mr-1.5" /> AI Strategy</TabsTrigger>
              )}
              {editing && (
                <TabsTrigger value="intelligence" data-testid="campaign-tab-intelligence"><Brain weight="duotone" className="h-3.5 w-3.5 mr-1.5" /> Intelligence</TabsTrigger>
              )}
              {editing && (
                <TabsTrigger value="landing" data-testid="campaign-tab-landing"><Storefront weight="duotone" className="h-3.5 w-3.5 mr-1.5" /> Landing Pages</TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="basics" className="mt-4 space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Name</label>
                <Input data-testid="campaign-form-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} maxLength={200} className="bg-graphite-950 border-white/10" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Product (optional)</label>
                  <select data-testid="campaign-form-product" value={form.product_id}
                    onChange={e => setForm({ ...form, product_id: e.target.value })}
                    className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                    <option value="">— None —</option>
                    {products.map(p => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Persona (optional)</label>
                  <select data-testid="campaign-form-persona" value={form.persona_id}
                    onChange={e => setForm({ ...form, persona_id: e.target.value })}
                    className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                    <option value="">— None —</option>
                    {personas.map(p => <option key={p.persona_id} value={p.persona_id}>{p.name}{p.market_type ? ` (${p.market_type})` : ""}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Type</label>
                  <select data-testid="campaign-form-type" value={form.type}
                    onChange={e => setForm({ ...form, type: e.target.value })}
                    className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                    <option value="">— None —</option>
                    <option value="B2B">B2B</option>
                    <option value="B2C">B2C</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Status</label>
                  <select data-testid="campaign-form-status" value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value })}
                    className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                    {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="audience" className="mt-4 space-y-3">
              {[
                ["target_audience", "Target audience"],
                ["icp_persona", "ICP / persona"],
                ["pain_point", "Pain point"],
                ["usp", "Unique selling proposition"],
                ["objective", "Objective"],
                ["desired_action", "Desired action"],
                ["geography", "Geography"],
              ].map(([k, label]) => (
                <div key={k} className="space-y-1.5">
                  <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">{label}</label>
                  <Textarea data-testid={`campaign-form-${k}`} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} rows={2} className="bg-graphite-950 border-white/10" />
                </div>
              ))}
            </TabsContent>

            <TabsContent value="budget" className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Budget</label>
                <Input data-testid="campaign-form-budget" type="number" step="0.01" min="0" value={form.budget}
                  onChange={e => setForm({ ...form, budget: e.target.value })} placeholder="0.00" className="bg-graphite-950 border-white/10" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Start date</label>
                <Input data-testid="campaign-form-start" type="date" value={form.start_date}
                  onChange={e => setForm({ ...form, start_date: e.target.value })} className="bg-graphite-950 border-white/10" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">End date</label>
                <Input data-testid="campaign-form-end" type="date" value={form.end_date}
                  onChange={e => setForm({ ...form, end_date: e.target.value })} className="bg-graphite-950 border-white/10" />
              </div>
            </TabsContent>

            <TabsContent value="assets" className="mt-4 space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Channels</label>
                {channels.length === 0 ? (
                  <div className="text-xs text-ink-secondary/70 border border-white/10 rounded-md p-3 bg-graphite-950">
                    No channels available. Ask a platform admin to add channels from the Platform page.
                  </div>
                ) : (
                  <div data-testid="campaign-form-channels" className="flex flex-wrap gap-2 border border-white/10 rounded-md p-3 bg-graphite-950">
                    {channels.map(ch => {
                      const active = form.channel_ids.includes(ch.channel_id);
                      return (
                        <button key={ch.channel_id} type="button"
                          data-testid={`campaign-channel-${ch.channel_id}`}
                          onClick={() => setForm({ ...form,
                            channel_ids: active
                              ? form.channel_ids.filter(x => x !== ch.channel_id)
                              : [...form.channel_ids, ch.channel_id] })}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                            active
                              ? "border-white/40 bg-white text-graphite-950"
                              : "border-white/10 bg-graphite-900 text-ink-secondary hover:text-ink-primary hover:bg-white/5"
                          }`}>
                          {ch.label}
                          <span className="ml-1.5 text-[9px] uppercase tracking-widest opacity-70">{ch.channel_type}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {form.preferred_channels && (
                <div className="space-y-1.5">
                  <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary flex items-center gap-2">
                    Legacy channels note
                    <span className="text-[9px] uppercase tracking-widest text-ink-secondary/60 border border-white/10 px-1.5 py-0.5 rounded-sm">legacy freeform</span>
                  </label>
                  <Textarea data-testid="campaign-form-preferred_channels" value={form.preferred_channels}
                    onChange={e => setForm({ ...form, preferred_channels: e.target.value })} rows={2}
                    className="bg-graphite-950 border-white/10 text-xs" />
                  <p className="text-[11px] text-ink-secondary/60">Kept from before the managed channels list. Clear this once you've picked the equivalent tags above.</p>
                </div>
              )}
              {[
                ["proof_points", "Proof points / case studies"],
                ["existing_assets", "Existing assets"],
              ].map(([k, label]) => (
                <div key={k} className="space-y-1.5">
                  <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">{label}</label>
                  <Textarea data-testid={`campaign-form-${k}`} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} rows={3} className="bg-graphite-950 border-white/10" />
                </div>
              ))}
            </TabsContent>

            {editing && editingCampaign && (
              <TabsContent value="performance" className="mt-4">
                <CampaignPerformance campaignId={editingCampaign.campaign_id} />
              </TabsContent>
            )}

            {editing && editingCampaign && (
              <TabsContent value="pillars" className="mt-4">
                <CampaignPillars campaignId={editingCampaign.campaign_id} />
              </TabsContent>
            )}

            {editing && editingCampaign && (
              <TabsContent value="content" className="mt-4">
                <CampaignContent campaignId={editingCampaign.campaign_id} />
              </TabsContent>
            )}

            {editing && editingCampaign && (
              <TabsContent value="strategy" className="mt-4">
                <CampaignStrategy campaign={editingCampaign} onCampaignUpdated={(updated) => {
                  setEditingCampaign(updated);
                  setItems(all => all.map(x => x.campaign_id === updated.campaign_id ? updated : x));
                }} />
              </TabsContent>
            )}

            {editing && editingCampaign && (
              <TabsContent value="intelligence" className="mt-4">
                <CampaignIntelligence campaignId={editingCampaign.campaign_id} />
              </TabsContent>
            )}

            {editing && editingCampaign && (
              <TabsContent value="landing" className="mt-4">
                <CampaignLandingPages campaignId={editingCampaign.campaign_id} campaignName={editingCampaign.name} />
              </TabsContent>
            )}
          </Tabs>

          <DialogFooter className="gap-2 mt-4">
            <button data-testid="campaign-form-cancel" onClick={() => { if (!saving) { setDialogOpen(false); setForm(emptyForm); setEditing(null); setEditingCampaign(null); } }} disabled={saving}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60">Close</button>
            {tab !== "strategy" && tab !== "pillars" && tab !== "content" && tab !== "performance" && tab !== "intelligence" && tab !== "landing" && (
              <button data-testid="campaign-form-save" onClick={save} disabled={saving || !form.name.trim()}
                className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60">
                {saving ? "Saving…" : editing ? "Save changes" : "Create campaign"}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={v => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="campaign-delete-dialog" className="bg-graphite-900 border-white/10 text-ink-primary">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove campaign?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary">{confirmDelete?.name} will be soft-deleted. Any linked leads keep their reference but stop appearing here.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="campaign-delete-cancel" className="bg-graphite-800 border-white/10 text-ink-primary hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="campaign-delete-confirm" onClick={doDelete} className="bg-red-400/90 text-graphite-950 hover:bg-red-300">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
